import React, { useEffect, useRef, useState } from 'react';
import { Badge, BlockStack, Button, Card, Divider, InlineStack, Spinner, Text } from '@shopify/polaris';
import { buildAdminUrl, BUNDLE_APPS, discountCode, discountRate, IBundleApp } from './config';
import AppChip from './AppChip';
import {
  StyledActionRow,
  StyledCodeBox,
  StyledSavingsBox,
  StyledStepNum,
  StyledStrike,
  StyledSummaryAppRow,
  StyledSummaryChip,
  StyledSummaryName,
  StyledSummaryOrigPrice,
  StyledSummaryPrice,
  StyledSummaryTier,
} from './styled';

interface ISummaryCardProps {
  appStates: { paypal: boolean; returns: boolean };
  planSelections: { trakow: string; paypal: string; returns: string };
  trakowOverride?: IBundleApp;
  addonOverrides?: IBundleApp[];
  onChargeTrakow?: () => void;
  isChargingTrakow?: boolean;
  shop?: string;
}

const SummaryCard = ({
  appStates,
  planSelections,
  trakowOverride,
  addonOverrides,
  onChargeTrakow,
  isChargingTrakow,
  shop,
}: ISummaryCardProps) => {
  const trakowApp = trakowOverride ?? BUNDLE_APPS[0];
  const addonApps: IBundleApp[] = addonOverrides ?? BUNDLE_APPS.slice(1);
  const [copied, setCopied] = useState(false);

  const includedAddOns = addonApps.filter((a) => appStates[a.id as 'paypal' | 'returns']);
  const addOnCount = Math.min(includedAddOns.length, 2) as 0 | 1 | 2;
  const rate = discountRate(addOnCount);
  const pct = Math.round(rate * 100);
  const code = discountCode(rate);

  const summaryApps = [trakowApp, ...addonApps.filter((a) => appStates[a.id as 'paypal' | 'returns'])];

  let totalOrig = 0;
  let totalDisc = 0;
  summaryApps.forEach((app) => {
    const plan = app.plans.find((p) => p.id === planSelections[app.id as 'trakow' | 'paypal' | 'returns']);
    if (plan && plan.price > 0) {
      totalOrig += plan.price;
      totalDisc += plan.price * (1 - rate);
    }
  });
  const save = totalOrig - totalDisc;

  const notInstalledApps = summaryApps.filter((a) => a.isInstalled === false);
  const allInstalled = notInstalledApps.length === 0;
  // Bundle is "ready" when at least one addon is included AND every included addon is installed.
  // When false: the Charge/Apply CTAs can't successfully apply the discount, so we disable them.
  const isBundleReady = includedAddOns.length > 0 && includedAddOns.every((a) => a.isInstalled);

  const getAction = (app: IBundleApp, _selectedPlanId: string): 'Install' | 'Charge' | 'Apply' => {
    // Trakow (current app) — always Charge, click navigates to Plan page
    if (app.id === 'trakow') return 'Charge';
    // Other apps: Install when not installed yet, Apply when already installed
    return app.isInstalled ? 'Apply' : 'Install';
  };

  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = () => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h3">
            Your post-purchase stack
          </Text>
          {rate > 0 ? (
            <Badge tone="success">{`${pct}% off`}</Badge>
          ) : (
            <Text variant="bodySm" tone="subdued" as="span">
              Add an app to complete your stack and unlock savings
            </Text>
          )}
        </InlineStack>

        <Divider />

        <BlockStack gap="300">
          {summaryApps.map((app) => {
            const plan =
              app.plans.find((p) => p.id === planSelections[app.id as 'trakow' | 'paypal' | 'returns']) ?? app.plans[0];
            const disc = plan.price > 0 ? plan.price * (1 - rate) : 0;

            return (
              <StyledSummaryAppRow key={app.id}>
                <StyledSummaryChip>
                  <AppChip color={app.color} letter={app.letter} label={app.name} logo={app.logo} size={22} />
                </StyledSummaryChip>
                <StyledSummaryName>
                  <Text variant="bodySm" fontWeight="semibold" as="span">
                    {app.name}
                  </Text>
                </StyledSummaryName>
                <StyledSummaryTier>{plan.name}</StyledSummaryTier>
                {plan.price === 0 ? (
                  <>
                    <StyledSummaryPrice $discounted={false} style={{ color: '#6d7175', fontWeight: 500 }}>
                      Free
                    </StyledSummaryPrice>
                    <StyledSummaryOrigPrice />
                  </>
                ) : (
                  <>
                    <StyledSummaryPrice $discounted={rate > 0}>
                      ${(rate > 0 ? disc : plan.price).toFixed(2)}/mo
                    </StyledSummaryPrice>
                    {rate > 0 && <StyledSummaryOrigPrice>${plan.price.toFixed(2)}</StyledSummaryOrigPrice>}
                  </>
                )}
              </StyledSummaryAppRow>
            );
          })}
        </BlockStack>

        {totalOrig > 0 && rate > 0 && (
          <>
            <Divider />
            <InlineStack align="space-between" blockAlign="baseline">
              <Text variant="bodySm" tone="subdued" as="span">
                Total/month
              </Text>
              <BlockStack gap="0" inlineAlign="end">
                <StyledStrike>${totalOrig.toFixed(2)}/mo</StyledStrike>
                <Text variant="headingLg" as="p">
                  ${totalDisc.toFixed(2)}/mo
                </Text>
              </BlockStack>
            </InlineStack>
            <StyledSavingsBox>
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="bodySm" fontWeight="semibold" tone="success" as="span">
                  You save
                </Text>
                <Text variant="bodyMd" fontWeight="semibold" tone="success" as="span">
                  ${save.toFixed(2)}/month
                </Text>
              </InlineStack>
            </StyledSavingsBox>
          </>
        )}

        {rate > 0 && (
          <>
            <Divider />
            <BlockStack gap="400">
              <BlockStack gap="150">
                <InlineStack gap="150" blockAlign="center">
                  <StyledStepNum>1</StyledStepNum>
                  <Text variant="headingSm" as="h4">
                    Copy discount code
                  </Text>
                </InlineStack>
                {!allInstalled && (
                  <Text variant="bodySm" tone="caution" as="p">
                    Install <strong>{notInstalledApps.map((a) => a.name).join(', ')}</strong> first to unlock the discount code.
                  </Text>
                )}
                <InlineStack gap="150" blockAlign="center">
                  <StyledCodeBox $locked={!allInstalled}>{code}</StyledCodeBox>
                  <Button variant={copied ? undefined : 'primary'} size="slim" onClick={handleCopy} disabled={!allInstalled}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </InlineStack>
                <Text variant="bodySm" tone="subdued" as="p">
                  Apply this inside each app when choosing your plan.
                </Text>
              </BlockStack>

              <BlockStack gap="150">
                <InlineStack gap="150" blockAlign="center">
                  <StyledStepNum>2</StyledStepNum>
                  <Text variant="headingSm" as="h4">
                    Install &amp; activate each app
                  </Text>
                </InlineStack>
                {(() => {
                  const trakowAction = getAction(trakowApp, planSelections.trakow);
                  const isDisabled = !isBundleReady;
                  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    if (isChargingTrakow || isDisabled) return;
                    if (trakowAction === 'Charge' && onChargeTrakow) onChargeTrakow();
                  };
                  const rowStyle: React.CSSProperties | undefined = isChargingTrakow
                    ? { pointerEvents: 'none', opacity: 0.7 }
                    : isDisabled
                    ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' }
                    : undefined;
                  return (
                    <StyledActionRow href="#" onClick={handleClick} style={rowStyle}>
                      <InlineStack gap="150" blockAlign="center" wrap={false}>
                        <AppChip
                          color={trakowApp.color}
                          letter={trakowApp.letter}
                          label={trakowApp.name}
                          logo={trakowApp.logo}
                          size={18}
                        />
                        <Text variant="bodySm" fontWeight="semibold" as="span">
                          {trakowApp.name}
                        </Text>
                      </InlineStack>
                      {isChargingTrakow ? (
                        <Spinner accessibilityLabel="Processing charge" size="small" />
                      ) : (
                        <InlineStack gap="050" blockAlign="center" wrap={false}>
                          <Text variant="bodySm" fontWeight="semibold" tone={isDisabled ? 'subdued' : 'success'} as="span">
                            {trakowAction}
                          </Text>
                          <span style={{ color: isDisabled ? '#6d7175' : '#008060' }}>→</span>
                        </InlineStack>
                      )}
                    </StyledActionRow>
                  );
                })()}
                {includedAddOns.map((app) => {
                  const action = getAction(app, planSelections[app.id as 'paypal' | 'returns']);
                  const isInstallAction = action === 'Install';
                  const applyHref = buildAdminUrl(shop, app.adminPath);
                  const href = isInstallAction ? app.storeUrl : applyHref || '#';
                  const hasHref = isInstallAction ? !!app.storeUrl : !!applyHref;
                  // Apply is only useful when the bundle is "ready" (all included addons installed);
                  // Install is always allowed since that's how the user fixes the missing app.
                  const isApplyDisabled = action === 'Apply' && !isBundleReady;
                  const rowStyle: React.CSSProperties | undefined = isApplyDisabled
                    ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' }
                    : undefined;
                  return (
                    <StyledActionRow
                      key={app.id}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={rowStyle}
                      {...(hasHref && !isApplyDisabled ? {} : { onClick: (e) => e.preventDefault() })}
                    >
                      <InlineStack gap="150" blockAlign="center" wrap={false}>
                        <AppChip color={app.color} letter={app.letter} label={app.name} logo={app.logo} size={18} />
                        <Text variant="bodySm" fontWeight="semibold" as="span">
                          {app.name}
                        </Text>
                      </InlineStack>
                      <InlineStack gap="050" blockAlign="center" wrap={false}>
                        <Text variant="bodySm" fontWeight="semibold" tone={isApplyDisabled ? 'subdued' : 'success'} as="span">
                          {action}
                        </Text>
                        <span style={{ color: isApplyDisabled ? '#6d7175' : '#008060' }}>→</span>
                      </InlineStack>
                    </StyledActionRow>
                  );
                })}
              </BlockStack>
            </BlockStack>
          </>
        )}
      </BlockStack>
    </Card>
  );
};

export default SummaryCard;
